import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import TopNav from "../../components/navigation/TopNav";
import { playSound } from "../../components/helpers/sound";

import { useTranslation } from "react-i18next";
import { maskString, maskFullNames } from "../../components/helpers/maskHelpers";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import StatusMessage from "../../components/ui/StatusMessage";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function UserRefundPayer() {
  const { t } = useTranslation();

  // =========================================================
  // VENDOR / BUS
  // =========================================================

  const [vendors, setVendors] = useState([]);
  const [buses, setBuses] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedBusId, setSelectedBusId] = useState("");
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingBuses, setLoadingBuses] = useState(false);

  // =========================================================
  // EXISTING DATA
  // =========================================================

  const [vendorWallets, setVendorWallets] = useState([]);
  const [refundablePayers, setRefundablePayers] = useState([]);

  const [selectedPayerKey, setSelectedPayerKey] = useState("");
  const [selectedVendorWalletId, setSelectedVendorWalletId] = useState("");
  const [selectedPayerWalletId, setSelectedPayerWalletId] = useState("");

  const [message, setMessage] = useState("");
  const [verdict, setVerdict] = useState("");
  const [lastAction, setLastAction] = useState("");

  const getPayerKey = (p) => String(p.payer?.userno ?? "");

  // =========================================================
  // RESET
  // =========================================================

  const resetResultState = () => {
    setMessage("");
    setVerdict("");
    setLastAction("");
  };

  const resetTransactionSelection = () => {
    setSelectedPayerKey("");
    setSelectedVendorWalletId("");
    setSelectedPayerWalletId("");
    resetResultState();
  };

  // =========================================================
  // FETCH VENDORS
  // =========================================================

  const fetchVendors = async () => {
    setLoadingVendors(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/vendors`);
      const vendorList = Array.isArray(res.data) ? res.data : [];

      setVendors(vendorList);
      setSelectedVendorId(vendorList.length === 1 ? String(vendorList[0].vendor_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible vendors", err);
      setVendors([]);
      setSelectedVendorId("");
    } finally {
      setLoadingVendors(false);
    }
  };

  // =========================================================
  // FETCH BUSES
  // =========================================================

  const fetchBuses = async (vendorId) => {
    if (!vendorId) {
      setBuses([]);
      setSelectedBusId("");
      return;
    }

    setLoadingBuses(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/user/buses`, {
        params: { vendor_id: vendorId },
      });

      const busList = Array.isArray(res.data) ? res.data : [];

      setBuses(busList);
      setSelectedBusId(busList.length === 1 ? String(busList[0].bus_id) : "");
    } catch (err) {
      console.error("Failed to fetch accessible buses", err);
      setBuses([]);
      setSelectedBusId("");
    } finally {
      setLoadingBuses(false);
    }
  };

  // =========================================================
  // EXISTING BACKEND DATA
  // =========================================================

  const fetchVendorWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/available-wallets`);
      setVendorWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌", t("user_refund_payer.messages.fetch_wallets_failed.text"), err);
      setVendorWallets([]);
    }
  };

  const fetchRefundablePayers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/driver/refundable-payers`);
      setRefundablePayers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌", t("user_refund_payer.messages.fetch_payers_failed.text"), err);
      setRefundablePayers([]);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

    fetchVendors();
    fetchVendorWallets();
    fetchRefundablePayers();
  }, []);

  // =========================================================
  // VENDOR / BUS CHANGES
  // =========================================================

  useEffect(() => {
    resetTransactionSelection();

    if (selectedVendorId) {
      fetchBuses(selectedVendorId);
    } else {
      setBuses([]);
      setSelectedBusId("");
    }
  }, [selectedVendorId]);

  useEffect(() => {
    resetTransactionSelection();
  }, [selectedBusId]);

  const handleVendorChange = (e) => {
    setSelectedVendorId(e.target.value);
    setSelectedBusId("");
    resetTransactionSelection();
  };

  const handleBusChange = (e) => {
    setSelectedBusId(e.target.value);
    resetTransactionSelection();
  };

  const vendorDropdownDisabled = loadingVendors || vendors.length <= 1;
  const busDropdownDisabled = loadingBuses || !selectedVendorId || buses.length <= 1;

  // =========================================================
  // BUS-FILTERED REFUNDABLE PAYERS
  // =========================================================

  const busRefundablePayers = useMemo(() => {
    if (!selectedBusId) return [];

    return refundablePayers.filter(
      (p) => String(p.bus_id) === String(selectedBusId)
    );
  }, [refundablePayers, selectedBusId]);

  // =========================================================
  // VENDOR WALLET LOOKUP
  // =========================================================

  const vendorWalletById = useMemo(() => {
    const map = new Map();

    vendorWallets.forEach((wallet) => {
      map.set(String(wallet.wallet_id), wallet);
    });

    return map;
  }, [vendorWallets]);

  // =========================================================
  // PAYER OPTIONS
  // =========================================================

  const payerOptions = useMemo(() => {
    const map = new Map();

    busRefundablePayers.forEach((p) => {
      const payerKey = getPayerKey(p);

      if (!payerKey || map.has(payerKey)) return;

      map.set(payerKey, {
        value: payerKey,
        label: [
          maskString(p.payer?.userno),
          maskFullNames(p.payer?.fullname),
          p.start_point,
          p.end_point,
          maskFullNames(p.payer?.sponsor),
        ].filter(Boolean).join(" | "),
      });
    });

    return Array.from(map.values());
  }, [busRefundablePayers]);

  const selectedPayerOption = useMemo(() => {
    return payerOptions.find(
      (option) => option.value === String(selectedPayerKey)
    ) || null;
  }, [payerOptions, selectedPayerKey]);

  const payerWalletRows = useMemo(() => {
    if (!selectedPayerKey) return [];

    return busRefundablePayers.filter(
      (p) => getPayerKey(p) === String(selectedPayerKey)
    );
  }, [busRefundablePayers, selectedPayerKey]);

  const canSubmit = Boolean(
    selectedVendorId &&
    selectedBusId &&
    selectedVendorWalletId &&
    selectedPayerWalletId
  );

  // =========================================================
  // MESSAGE HELPERS
  // =========================================================

  const extractValueFromMessage = (backendMessage, key) => {
    if (!backendMessage) return "";

    const match = backendMessage.match(new RegExp(`${key}=([^\\s|]+)`));
    return match?.[1] ?? "";
  };

  const getRouteLabel = (selectedRow, vendorWallet) => {
    return selectedRow?.route_name || vendorWallet?.route_name || "-";
  };

  const buildSuccessMessage = (resultStatus, selectedRow, vendorWallet, data) => {
    const backendMessage = data?.message || "";

    const payerFullname = selectedRow?.payer?.fullname || "";
    const route = getRouteLabel(selectedRow, vendorWallet);

    const payerTicketBalanceBefore =
      data?.to_ticket_balance_before ??
      extractValueFromMessage(backendMessage, "to_ticket_balance_before") ??
      "";

    const payerTicketBalanceAfter =
      data?.to_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "to_ticket_balance_after") ??
      "";

    return [
      `${t("user_refund_payer.messages.approved.result.label")}: ${t(`user_refund_payer.messages.approved.result.${resultStatus}`) || resultStatus}`,
      `${t("user_refund_payer.messages.approved.fullname.label")}: ${payerFullname}`,
      `${t("user_refund_payer.messages.approved.route.label")}: ${route}`,
      `${t("user_refund_payer.messages.approved.ticket_balance_before.label")}: ${payerTicketBalanceBefore}`,
      `${t("user_refund_payer.messages.approved.ticket_balance_after.label")}: ${payerTicketBalanceAfter}`,
    ].join(" | ");
  };

  const buildCancelledMessage = (selectedRow, vendorWallet, data) => {
    const backendMessage = data?.message || "";

    const payerFullname = selectedRow?.payer?.fullname || "";
    const route = getRouteLabel(selectedRow, vendorWallet);

    const resultStatus =
      data?.verdict ||
      extractValueFromMessage(backendMessage, "result_status") ||
      "cancelled";

    const ticketBalanceBefore =
      data?.from_ticket_balance_before ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_before") ??
      extractValueFromMessage(backendMessage, "ticket_balance_before") ??
      selectedRow?.ticket_balance ??
      "";

    const ticketBalanceAfter =
      data?.from_ticket_balance_after ??
      extractValueFromMessage(backendMessage, "from_ticket_balance_after") ??
      "";

    const reasonKey = backendMessage.includes("Not enough ticket balance")
      ? "Not enough ticket balance"
      : "cancelled";

    return [
      `${t("user_refund_payer.messages.cancelled.result.label")}: ${t(`user_refund_payer.messages.cancelled.result.${resultStatus}`) || resultStatus}`,
      `${t("user_refund_payer.messages.cancelled.reason.label")}: ${t(`user_refund_payer.messages.cancelled.reason.${reasonKey}`) || reasonKey}`,
      `${t("user_refund_payer.messages.cancelled.fullname.label")}: ${payerFullname}`,
      `${t("user_refund_payer.messages.cancelled.route.label")}: ${route}`,
      `${t("user_refund_payer.messages.cancelled.ticket_balance_before.label")}: ${ticketBalanceBefore}`,
      `${t("user_refund_payer.messages.cancelled.ticket_balance_after.label")}: ${ticketBalanceAfter}`,
    ].join(" | ");
  };

  // =========================================================
  // PAYER / WALLET SELECTION
  // =========================================================

  const handlePayerChange = (option) => {
    const payerKey = option ? option.value : "";

    setSelectedPayerKey(payerKey);
    setSelectedVendorWalletId("");
    setSelectedPayerWalletId("");
    resetResultState();

    if (!payerKey) return;

    const wallets = busRefundablePayers.filter(
      (p) => getPayerKey(p) === String(payerKey)
    );

    if (wallets.length === 1) {
      setSelectedPayerWalletId(String(wallets[0].payer_wallet_id));
      setSelectedVendorWalletId(String(wallets[0].vendor_wallet_id));
    }
  };

  const handleWalletChange = (e) => {
    const payerWalletId = e.target.value;

    setSelectedPayerWalletId(payerWalletId);
    setSelectedVendorWalletId("");
    resetResultState();

    if (!payerWalletId) return;

    const selectedRow = payerWalletRows.find(
      (p) => String(p.payer_wallet_id) === String(payerWalletId)
    );

    if (!selectedRow) return;

    setSelectedVendorWalletId(String(selectedRow.vendor_wallet_id));
  };

  // =========================================================
  // REFUND
  // =========================================================

  const handleManualRefund = async () => {
    if (!canSubmit) {
      setMessage(t("user_refund_payer.messages.missing_wallets.text"));
      setVerdict("cancelled");
      playSound("error");
      return;
    }

    const selectedRow = busRefundablePayers.find(
      (p) =>
        String(p.payer_wallet_id) === String(selectedPayerWalletId) &&
        String(p.vendor_wallet_id) === String(selectedVendorWalletId)
    );

    const selectedVendorWallet = vendorWalletById.get(
      String(selectedVendorWalletId)
    );

    try {
      const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/driver/refund-on-bus`, {
        from_wallet_id: Number(selectedVendorWalletId),
        delta_tickets_from_wallet: -1,
        delta_money_from_wallet: 0,
        to_wallet_id: Number(selectedPayerWalletId),
        delta_tickets_to_wallet: 1,
        delta_money_to_wallet: 0,
        header: "refund-on-bus",
      });

      const resultStatus = String(data?.verdict || "approved").toLowerCase();
      const isSuccess = resultStatus === "approved" || resultStatus === "success";

      if (isSuccess) {
        setMessage(
          buildSuccessMessage(
            resultStatus,
            selectedRow,
            selectedVendorWallet,
            data
          )
        );

        await fetchVendorWallets();
        await fetchRefundablePayers();
      } else {
        setMessage(
          buildCancelledMessage(
            selectedRow,
            selectedVendorWallet,
            data
          )
        );
      }

      setVerdict(isSuccess ? "approved" : "cancelled");
      playSound(isSuccess ? "success" : "error");
    } catch (err) {
      const errData = err.response?.data || {};

      const errMsg =
        errData?.message ||
        errData?.detail ||
        t("user_refund_payer.messages.failure_fallback.text");

      setMessage(
        buildCancelledMessage(
          selectedRow,
          selectedVendorWallet,
          {
            verdict: "cancelled",
            message: errMsg,
          }
        )
      );

      setVerdict("cancelled");
      playSound("error");
    } finally {
      setSelectedPayerKey("");
      setSelectedVendorWalletId("");
      setSelectedPayerWalletId("");
    }
  };

  const messageVariant =
    verdict === "approved" ? "success" : message ? "error" : "info";

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <StandardScreen
      topNav={<TopNav title={t("user_refund_payer.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">

            <Card title={t("user_refund_payer.title")}>
              <Stack gap="3">

                <FormField label={t("user_refund_payer.fields.vendor.label")}>
                  <SelectInput
                    value={selectedVendorId}
                    disabled={vendorDropdownDisabled}
                    onChange={handleVendorChange}
                  >
                    <option value="">
                      {loadingVendors
                        ? t("user_refund_payer.fields.vendor.loading")
                        : t("user_refund_payer.fields.vendor.placeholder")}
                    </option>

                    {vendors.map((vendor) => (
                      <option key={vendor.vendor_id} value={vendor.vendor_id}>
                        {vendor.vendor_id} | {vendor.vendor_name}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("user_refund_payer.fields.bus.label")}>
                  <SelectInput
                    value={selectedBusId}
                    disabled={busDropdownDisabled}
                    onChange={handleBusChange}
                  >
                    <option value="">
                      {loadingBuses
                        ? t("user_refund_payer.fields.bus.loading")
                        : t("user_refund_payer.fields.bus.placeholder")}
                    </option>

                    {buses.map((bus) => (
                      <option key={bus.bus_id} value={bus.bus_id}>
                        {bus.bus_id} | {bus.plate_number}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("user_refund_payer.fields.payer.label")}>
                  <Select
                    classNamePrefix="react-select"
                    options={payerOptions}
                    value={selectedPayerOption}
                    onChange={handlePayerChange}
                    placeholder={t("user_refund_payer.fields.payer.placeholder")}
                    isClearable
                    isDisabled={!selectedBusId}
                  />
                </FormField>

                <FormField label={t("user_refund_payer.fields.wallet.label")}>
                  <SelectInput
                    value={selectedPayerWalletId}
                    disabled={!selectedPayerKey || payerWalletRows.length <= 1}
                    onChange={handleWalletChange}
                  >
                    <option value="">
                      {t("user_refund_payer.fields.wallet.placeholder")}
                    </option>

                    {payerWalletRows.map((row) => {
                      const vendorWallet = vendorWalletById.get(String(row.vendor_wallet_id));

                      return (
                        <option key={row.payer_wallet_id} value={row.payer_wallet_id}>
                          {[
                            row.route_name || vendorWallet?.route_name,
                            row.start_point && row.end_point
                              ? `${row.start_point} → ${row.end_point}`
                              : null,
                            `${t("user_refund_payer.dropdown_item.ticket_balance_before")}: ${row.ticket_balance ?? 0}`,
                            vendorWallet?.wallet_id
                              ? `${t("user_refund_payer.dropdown_item.route_money_box_wallet")}: #${vendorWallet.wallet_id}`
                              : null,
                          ].filter(Boolean).join(" | ")}
                        </option>
                      );
                    })}
                  </SelectInput>
                </FormField>

                <Button
                  onClick={() => {
                    setLastAction("refund");
                    handleManualRefund();
                  }}
                  disabled={!canSubmit || (lastAction === "refund" && message !== "")}
                  fullWidth
                >
                  {lastAction === "refund"
                    ? verdict === "approved"
                      ? t("user_refund_payer.actions.success")
                      : verdict === "cancelled"
                      ? t("user_refund_payer.actions.failure")
                      : t("user_refund_payer.actions.submit")
                    : t("user_refund_payer.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />

              </Stack>
            </Card>

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}